import { applyRoundAndFormat } from '../../../utils';

// Mirrors the enhanced (v2) Order Planning item card lines from OrderForm/OrderFormRow.tsx
// so order screens render the same Order / On Order / On Hand detail lines.

type ConvertibleItem = {
  packSize?: string | number | null;
  quantityInUom?: number | null;
  caseSize?: number | null;
};

export type ItemCardData = ConvertibleItem & {
  price?: number | null;
  primaryUom?: string | null;
  orderableUom?: string | null;
  stockOnOrder?: number | null;
  stockOnHand?: number | null;
};

export type ItemCardLabels = {
  order: string;
  onOrder: string;
  onHand: string;
};

const hasText = (value?: string | null): value is string => typeof value === 'string' && value.trim().length > 0;

const isValidNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const parseNumberLike = (value: unknown): number | null => {
  if (isValidNumber(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.replace(/,/g, '').trim();
    if (normalizedValue.length === 0) {
      return null;
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
};

const joinTextParts = (parts: Array<string | null | undefined>, separator = ' ') => {
  return parts.filter((part): part is string => hasText(part ?? '')).join(separator).trim();
};

const formatNumericValue = (value: number) => {
  if (Number.isInteger(value)) {
    return value.toString();
  }

  return value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

export const getOrderableConversionCount = (item: ConvertibleItem): number | null => {
  const candidates = [
    parseNumberLike(item.packSize),
    parseNumberLike(item.quantityInUom),
    parseNumberLike(item.caseSize),
  ];

  const conversionCount = candidates.find((candidate) => isValidNumber(candidate) && candidate > 0);
  return conversionCount ?? null;
};

const formatConversionDisplay = (count: number | null, primaryUom?: string | null, orderableUom?: string | null) => {
  if (count == null || !hasText(primaryUom) || !hasText(orderableUom)) {
    return null;
  }

  if (Math.abs(count - 1) < 0.0001) {
    return null;
  }

  return `${formatNumericValue(count)} ${primaryUom.trim()}`;
};

const formatCostDisplay = (price: number, currencyCode: string, showMessageWhenNoCost: boolean) => {
  if (showMessageWhenNoCost || !isValidNumber(price) || price <= 0) {
    return null;
  }

  return hasText(currencyCode)
    ? applyRoundAndFormat(price, currencyCode)
    : formatNumericValue(price);
};

const formatOrderLine = (
  orderLabel: string,
  costDisplay: string | null,
  orderableUom?: string | null,
  conversionDisplay?: string | null,
) => {
  const cleanOrderableUom = hasText(orderableUom) ? orderableUom.trim() : null;

  if (!costDisplay && !cleanOrderableUom) {
    return null;
  }

  const baseText = costDisplay && cleanOrderableUom
    ? `${joinTextParts([orderLabel, costDisplay])} / ${cleanOrderableUom}`
    : joinTextParts([orderLabel, costDisplay ?? cleanOrderableUom]);

  if (!hasText(baseText)) {
    return null;
  }

  return conversionDisplay ? `${baseText} (${conversionDisplay})` : baseText;
};

const formatStockLine = (
  label: string,
  quantity: unknown,
  primaryUom?: string | null,
  convertedQuantity?: number | null,
  convertedUom?: string | null,
) => {
  const numericQuantity = parseNumberLike(quantity);

  if (numericQuantity == null) {
    return null;
  }

  const primaryDisplay = joinTextParts([formatNumericValue(numericQuantity), hasText(primaryUom) ? primaryUom.trim() : null]);

  if (!hasText(primaryDisplay)) {
    return null;
  }

  const convertedDisplay = isValidNumber(convertedQuantity) && hasText(convertedUom)
    ? joinTextParts([formatNumericValue(convertedQuantity), convertedUom.trim()])
    : null;

  return convertedDisplay ? `${label}: ${primaryDisplay} (${convertedDisplay})` : `${label}: ${primaryDisplay}`;
};

export const buildItemCardLines = (
  item: ItemCardData,
  currencyCode: string | undefined,
  labels: ItemCardLabels,
  options?: { showMessageWhenNoCost?: boolean },
): string[] => {
  const orderableConversionCount = getOrderableConversionCount(item);
  const conversionDisplay = formatConversionDisplay(orderableConversionCount, item.primaryUom, item.orderableUom);
  const costDisplay = formatCostDisplay(item.price ?? 0, currencyCode ?? '', options?.showMessageWhenNoCost ?? false);
  const orderLine = formatOrderLine(labels.order, costDisplay, item.orderableUom, conversionDisplay);

  const onOrderLine = formatStockLine(labels.onOrder, item.stockOnOrder, item.orderableUom);

  const stockOnHandValue = parseNumberLike(item.stockOnHand);
  const hasOnHandConversion = stockOnHandValue != null
    && orderableConversionCount != null
    && orderableConversionCount > 0
    && Math.abs(orderableConversionCount - 1) >= 0.0001
    && hasText(item.orderableUom);
  const onHandConvertedQuantity = hasOnHandConversion ? stockOnHandValue! / orderableConversionCount! : null;
  const onHandLine = formatStockLine(
    labels.onHand,
    onHandConvertedQuantity ?? stockOnHandValue,
    hasText(item.orderableUom) ? item.orderableUom : item.primaryUom,
    hasOnHandConversion ? stockOnHandValue : null,
    item.primaryUom,
  );

  return [orderLine, onOrderLine, onHandLine].filter((line): line is string => hasText(line ?? ''));
};
